import { FC, Fragment, useState } from 'react'
import { useParams } from 'react-router-dom'
import { getUserPermissions } from '../../../../../../utils/reducers/getters'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import useEditDeleteModal from '../../../../../../utils/hooks/useEditDeleteModal'
import ModalEditCustomerProject from '../../../../ModalEditCustomerProject'
import { TableInstanceType } from '../../../../../../baseComponents/TwinTable/types'
import useUpdateTableInstance from '../../../../../../utils/hooks/useUpdateTableInstance'
import ModalCustomerQuotesCreateConnect from '../../../CustomerSellOrders/CustomerQuotesCreateModal'
import { CustomerModelType } from '@teinor/erp/types/company/customer'
import './customerSellOrdersDashboard.sass'
import { ComponentWithPermissions } from '../../../../../../baseComponents/CreateRoutering/types'
import { CustomerDashboardAddButton, CustomerDashboardSeeAllLink } from '../../SpecificComponents'
import { CustomerTypesInversedKeys } from '@teinor/erp/types/company/customer/customerTypes'
import TableCustomerQuoteOrdersConnect from '../../../CustomerSellOrders/TableCustomerQuotes'
import { TwinDictionary } from '../../../../../../utils/globals/dictionary'
import { RightHeaderCustomerSellOrder } from '../../../CustomerSellOrders'
import TableCustomerOrdersConnect from '../../../CustomerSellOrders/TableCustomerOrders'
import ModalCreateCustomerSellOrderConnect from '../../../CustomerSellOrders/ModalCreateCustomerSellOrder'
import TableCustomerDeliveryNotesConnect from '../../../CustomerSellOrders/TableCustomerDeliveryNotes'
import ModalCreateCustomerSellDeliveryNoteConnect from '../../../CustomerSellOrders/ModalCreateCustomerSellDeliveryNote'
import TableCustomerInvoicesConnect from '../../../CustomerSellOrders/TableCustomerInvoices'
import ModalCreateCustomerSellInvoiceConnect from '../../../CustomerSellOrders/ModalCreateCustomerSellInvoice'
import { CustomerSellOrderComponentTyExtended } from '../../../CustomerSellOrders/types'
import { ModalEditComponentProps } from '../../../../../../baseComponents/ModalsLayouts/types'
import { CustomerQuoteOrdersStats, CustomerSellDeliveryNotesStats, CustomerSellInvoicesStats, CustomerSellOrderStats, CustomerSellTicketsStats } from './CustomerSellOrdersStats'
import TableCustomerTickets from '../../../CustomerSellOrders/TableCustomerTickets'
import NullComponent from '../../../../../../baseComponents/NullComponent'

interface CustomerSellOrdersDashboardProps {
    title: string
    allRowData: CustomerModelType
}

const CustomerSellOrdersDashboard: React.FC<CustomerSellOrdersDashboardProps> = ({ ...rest }) => {
    return (
        <div className='flex flex-auto flex-col gap-5 customer_quote_order_dashboard'>
            <TableCustomerSellOrdersDashboard {...rest} />
        </div>
    )
}

interface TableCustomerSellOrdersDashboardProps extends TableCustomerSellOrdersDashboardLogicProps {
    title: string
    allRowData: CustomerModelType
}

const TableCustomerSellOrdersDashboard: React.FC<TableCustomerSellOrdersDashboardProps> = ({ allRowData, ...rest }) => {
    const { id } = useParams()
    const {t} = useTwinTranslation()
    const { openModal, setOpenModal } = useEditDeleteModal()
    const { tableInstance, setTableInstance, setTypeSell, typeSell } = useTableCustomerSellOrdersDashboardLogic()
    const { updateTableInstance } = useUpdateTableInstance({ tableInstance })
    const userPermissions = getUserPermissions('customer.profile.quotes') || { n: 4, permission: 'delete' }

    const dictCustomerSellTy: CustomerSellOrderComponentTyExtended = {
        0: {
            TableComponent: TableCustomerQuoteOrdersConnect,
            CreateModalComponent: ModalCustomerQuotesCreateConnect,
            extraWhereParams: { 'QuoteOrder.CustomerId': id, is_last: true },
            newText: t('newQuote', 'Nuevo presupuesto'),
            text: t('quotes', 'Presupuestos'),
            ComponentStats: CustomerQuoteOrdersStats,
            tableName: 'QuoteOrders'
        },
        1: {
            TableComponent: TableCustomerOrdersConnect,
            CreateModalComponent: ModalCreateCustomerSellOrderConnect,
            extraWhereParams: { 'SellOrder.CustomerId': id, is_last: true },
            newText: t('newSellOrderS', 'Nuevo pedido'),
            text: t('orders', 'Pedidos'),
            ComponentStats: CustomerSellOrderStats,
            tableName: 'SellOrders'
        },
        2: {
            TableComponent: TableCustomerDeliveryNotesConnect,
            CreateModalComponent: ModalCreateCustomerSellDeliveryNoteConnect,
            extraWhereParams: { 'SellDeliveryNote.CustomerId': id, is_last: true },
            newText: t('newDeliveryNote', 'Nuevo albarán'),
            text: t('deliveryNotes', 'Albaranes'),
            ComponentStats: CustomerSellDeliveryNotesStats,
            tableName: 'SellDeliveryNotes'
        },
        3: {
            TableComponent: TableCustomerInvoicesConnect,
            CreateModalComponent: ModalCreateCustomerSellInvoiceConnect,
            extraWhereParams: { 'SellInvoice.CustomerId': id, is_last: true },
            newText: t('newInvoice', 'Nueva factura'),
            text: t('invoices', 'Facturas'),
            ComponentStats: CustomerSellInvoicesStats,
            tableName: 'SellInvoices'
        },
        4: {
            TableComponent: TableCustomerTickets,
            CreateModalComponent: NullComponent,
            extraWhereParams: { 'SellTicket.CustomerId': id, 'SellTicket.is_last': true },
            newText: undefined,
            text: t('tickets', 'Tickets'),
            ComponentStats: CustomerSellTicketsStats,
            tableName: 'SellTickets'
        }
    }
    const currentTypeSell = dictCustomerSellTy[typeSell]
    const TableComponent: React.FC<any> = currentTypeSell.TableComponent
    const StatsComponent: React.FC<any> = currentTypeSell.ComponentStats
    return (
        <Fragment>
            <CustomerSellOrderHeader userPermissions={userPermissions || { n: 4, permission: 'delete' }} customerType={allRowData?.customer_type} title={currentTypeSell.text || ''} updateTableInstance={updateTableInstance} basicData={{ setTypeSell, typeSell }} CreateModalComponent={currentTypeSell.CreateModalComponent} buttonText={currentTypeSell.newText} />
            <StatsComponent />
            <TableComponent name={'customersShopsDashboard ' + currentTypeSell.tableName} hideLoadFiltersButton={true} className='small_simple_table table_customer_order_ds' setTableInstance={setTableInstance} extraWhereParams={currentTypeSell.extraWhereParams} setOpened={setOpenModal} tableInstance={tableInstance} userPermissions={userPermissions} {...rest} />
            {openModal?.type === 'edit' && <ModalEditCustomerProject {...{ setOpened: () => setOpenModal(null) }} onSubmit={updateTableInstance} userPermissions={userPermissions} allRowData={openModal?.allRowData} basicData={{}} />}
        </Fragment>
    )
}

interface TableCustomerSellOrdersDashboardLogicProps { }

const useTableCustomerSellOrdersDashboardLogic = () => {
    const [tableInstance, setTableInstance] = useState<TableInstanceType>(null)
    const [typeSell, setTypeSell] = useState<CustomerTypesInversedKeys>(0)

    return { setTableInstance, tableInstance, typeSell, setTypeSell }
}

interface CustomerSellOrderHeaderProps extends ComponentWithPermissions {
    title: string
    customerType: CustomerTypesInversedKeys
    updateTableInstance: () => void
    basicData: TwinDictionary
    CreateModalComponent: FC<ModalEditComponentProps>
    buttonText: string | undefined
}

const CustomerSellOrderHeader: React.FC<CustomerSellOrderHeaderProps> = ({ title, customerType, userPermissions, updateTableInstance, basicData, CreateModalComponent, buttonText }) => {
    return (
        <div className='flex flex-wrap justify-between gap-5'>
            <div className='flex items-center flex-wrap gap-x-10'>
                <h3>{title}</h3>
                <RightHeaderCustomerSellOrder basicData={basicData} onSubmit={() =>{}} setOpened={() =>{}} />
                <CustomerDashboardSeeAllLink customerTab='sells' customerType={customerType} />
            </div>
            {buttonText ? <CustomerDashboardAddButton userPermissions={userPermissions} CreateModalComponent={CreateModalComponent} onSubmit={updateTableInstance} text={buttonText} /> : null}
        </div>
    )
}

export default CustomerSellOrdersDashboard

